import React, { useEffect, useRef, useState } from 'react';
import { Box, FormControlLabel, IconButton, Stack, Switch, TextField, Typography, alpha, useTheme } from '@mui/material';
import { CloudUploadOutlined, PictureAsPdfOutlined, Close } from '@mui/icons-material';
import SettingsDialog from '../common/SettingsDialog';
import SearchableSelect from '../common/SearchableSelect';
import { LibraryDocument } from '../../types';
import { formatFileSize } from '../../utils/fileSize';

/** The server's upload limit (MAX_FILE_SIZE, 5 MB by default — decimal, so said as "5 MB"). */
const MAX_BYTES = 5_000_000;
const MAX_LABEL = '5 MB';

export interface LibraryDocumentInput {
  title: string;
  file?: File;
  isGlobal: boolean;
  schoolId: string;
}

interface Props {
  open: boolean;
  /** "policy", "SOP" — in the dialog's words. */
  noun: string;
  /** The document being edited; absent when uploading a new one. */
  editing: LibraryDocument | null;
  /** A system admin decides whether it is for every school or one; a school's staff always upload for their own. */
  schools?: Array<{ value: string; label: string }>;
  onClose: () => void;
  onSave: (input: LibraryDocumentInput) => Promise<void>;
}

/** Upload a policy or SOP (a title and a PDF), or rename one. */
const LibraryDocumentDialog: React.FC<Props> = ({ open, noun, editing, schools, onClose, onSave }) => {
  const theme = useTheme();
  const input = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [isGlobal, setIsGlobal] = useState(false);
  const [schoolId, setSchoolId] = useState('');
  const [dragging, setDragging] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTitle(editing?.title ?? '');
    setFile(null);
    setIsGlobal(editing?.isGlobal ?? false);
    setSchoolId(editing?.schoolId?._id ?? '');
    setProblem(null);
  }, [open, editing]);

  const pick = (f: File | undefined) => {
    if (!f) return;
    if (f.type !== 'application/pdf' && !/\.pdf$/i.test(f.name)) { setProblem('Choose a PDF file.'); return; }
    if (f.size > MAX_BYTES) { setProblem(`The file is ${formatFileSize(f.size)}; the largest allowed is ${MAX_LABEL}.`); return; }
    setProblem(null);
    setFile(f);
    // A new upload takes its title from the file's name until the user types one.
    if (!title.trim()) setTitle(f.name.replace(/\.pdf$/i, '').replace(/[_-]+/g, ' ').trim());
  };

  const missing = !title.trim() ? 'Give it a title.'
    : !editing && !file ? 'Choose the PDF.'
      : schools && !isGlobal && !schoolId ? 'Choose the school.'
        : null;

  const save = async () => {
    if (missing) { setProblem(missing); return; }
    setSaving(true);
    try {
      await onSave({ title: title.trim(), file: file ?? undefined, isGlobal, schoolId });
    } finally {
      setSaving(false);
    }
  };

  return (
    <SettingsDialog
      open={open}
      title={editing ? `Rename ${noun}` : `Upload ${noun}`}
      subtitle={editing ? `The PDF stays as it is — to change it, upload a new ${noun} and remove this one.` : `A PDF your staff can read and download.`}
      onClose={onClose}
      onSave={save}
      saving={saving}
      saveLabel={editing ? 'Save' : 'Upload'}
    >
      <TextField label="Title" value={title} onChange={e => { setTitle(e.target.value); setProblem(null); }} fullWidth size="small" />

      {!editing && (
        file ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ p: 1.5, borderRadius: 2, border: 1, borderColor: 'divider' }}>
            <Box sx={{ width: 36, height: 36, borderRadius: 1.5, display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.error.main, 0.08), color: 'error.main' }}>
              <PictureAsPdfOutlined fontSize="small" />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>{file.name}</Typography>
              <Typography variant="caption" color="text.secondary">{formatFileSize(file.size)}</Typography>
            </Box>
            <IconButton size="small" onClick={() => setFile(null)} aria-label="Remove file"><Close fontSize="small" /></IconButton>
          </Stack>
        ) : (
          <Box
            role="button"
            tabIndex={0}
            onClick={() => input.current?.click()}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.current?.click(); } }}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files?.[0]); }}
            sx={{
              py: 3.5, px: 2, textAlign: 'center', cursor: 'pointer', borderRadius: 2,
              border: `1.5px dashed ${dragging ? theme.palette.primary.main : theme.palette.divider}`,
              bgcolor: dragging ? alpha(theme.palette.primary.main, 0.04) : alpha(theme.palette.text.primary, 0.015),
              transition: 'border-color 120ms, background-color 120ms',
              '&:hover, &:focus-visible': { borderColor: 'primary.main', outline: 'none' },
            }}
          >
            <CloudUploadOutlined sx={{ fontSize: 30, color: dragging ? 'primary.main' : 'text.disabled', mb: 0.5 }} />
            <Typography variant="body2" fontWeight={600}>Drop the PDF here, or click to choose</Typography>
            <Typography variant="caption" color="text.secondary">PDF only · up to {MAX_LABEL}</Typography>
          </Box>
        )
      )}
      <input ref={input} type="file" accept="application/pdf,.pdf" hidden onChange={e => { pick(e.target.files?.[0]); e.target.value = ''; }} />

      {schools && (
        <Box>
          <FormControlLabel
            control={<Switch checked={isGlobal} onChange={e => { setIsGlobal(e.target.checked); setProblem(null); }} />}
            label={<Typography variant="body2">For every school</Typography>}
          />
          {!isGlobal && (
            <Box sx={{ mt: 1 }}>
              <SearchableSelect label="School" value={schoolId} onChange={v => { setSchoolId(v); setProblem(null); }} options={schools} placeholder="Choose the school" disabled={!!editing} />
            </Box>
          )}
        </Box>
      )}

      {problem && <Typography variant="body2" color="error.main">{problem}</Typography>}
    </SettingsDialog>
  );
};

export default LibraryDocumentDialog;
